/**
 * Section + Toolbar — page composition helpers.
 *
 * `Section` is a labeled content region (optional title + description + actions)
 * for grouping related content on a page. `Toolbar` is a horizontal row of
 * controls (filters, search, actions) that wraps gracefully on narrow widths.
 *
 *   <Section title="Allowed origins" description="Domains that may embed this widget.">
 *     …
 *   </Section>
 *
 *   <Toolbar>
 *     <Select …/>
 *     <Input …/>
 *     <Toolbar.Spacer />
 *     <Button>New</Button>
 *   </Toolbar>
 */
import type { HTMLAttributes, ReactElement, ReactNode } from 'react';

import { cn } from './cn.js';

export interface SectionProps
  extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode;
  description?: ReactNode;
  /** Right-aligned actions in the section header. */
  actions?: ReactNode;
}

export function Section({
  title,
  description,
  actions,
  className,
  children,
  ...props
}: SectionProps): ReactElement {
  return (
    <section className={cn('mb-6 flex flex-col gap-3', className)} {...props}>
      {title || actions ? (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-col gap-1">
            {title ? (
              <h2 className="text-base font-semibold text-text">{title}</h2>
            ) : null}
            {description ? (
              <p className="text-sm text-muted">{description}</p>
            ) : null}
          </div>
          {actions ? (
            <div className="flex flex-wrap items-center gap-2">{actions}</div>
          ) : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export type ToolbarProps = HTMLAttributes<HTMLDivElement>;

export function Toolbar({
  className,
  children,
  ...props
}: ToolbarProps): ReactElement {
  return (
    <div
      role="toolbar"
      className={cn('flex flex-wrap items-center gap-2', className)}
      {...props}
    >
      {children}
    </div>
  );
}

/** Pushes subsequent toolbar items to the right edge. */
function ToolbarSpacer(): ReactElement {
  return <div className="ml-auto" aria-hidden="true" />;
}

Toolbar.Spacer = ToolbarSpacer;
